import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * ScoringBadge — Composant réutilisable pour afficher les scores Altman/Piotroski/Beneish
 * avec graceful degradation si les champs ne sont pas encore exposés par l'API.
 * 
 * Props (tous optionnels):
 * - score: number | null | undefined
 * - zone: string | null | undefined ("safe" | "grey" | "distress" | "not_applicable_financials" | ...)
 * - coverage: string | null | undefined ("9/9" | "8/8" | "7/8" | "insufficient" | ...)
 * - model: "altman" | "piotroski" | "beneish"
 * - exclusionReason: string | null | undefined ("financial_sector" | "no_data_vn" | ...)
 */
const ScoringBadge = ({ score, zone, coverage, model, exclusionReason }) => {
  // Graceful fallback: if no data at all, render em-dash
  if (score === undefined && zone === undefined && coverage === undefined && exclusionReason === undefined) {
    return <span className="text-gray-500">—</span>;
  }

  // Vietnam data pending
  if (exclusionReason === 'no_data_vn') {
    return (
      <span 
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
      >
        Données en attente
      </span>
    );
  }

  // Financial sector exclusion
  if (zone === 'not_applicable_financials') {
    return (
      <span 
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}
      >
        N/A — Financières
      </span>
    );
  }

  // Insufficient data
  if (coverage === 'insufficient') {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <span className="text-gray-500 cursor-help">—</span>
          </TooltipTrigger>
          <TooltipContent className="bg-gray-900 text-white text-xs px-2 py-1">
            Données insuffisantes pour calculer
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // No score available (but other fields may exist)
  if (score === undefined || score === null) {
    return <span className="text-gray-500">—</span>;
  }

  // Format score based on model
  const formatScore = () => {
    if (model === 'piotroski') {
      return Math.round(score).toString();
    }
    if (model === 'beneish') {
      // Use minus sign (−) not hyphen (-) for negative numbers
      const formatted = score.toFixed(2);
      if (score < 0) {
        return '−' + Math.abs(score).toFixed(2);
      }
      return formatted;
    }
    // altman or default
    return score.toFixed(2);
  };

  // Get color based on zone and model
  const getScoreColor = () => {
    // No zone = no coloration (backend in transition)
    if (!zone) {
      return undefined;
    }

    if (model === 'altman') {
      if (zone === 'safe') return '#059669';
      if (zone === 'grey') return '#D97706';
      if (zone === 'distress') return '#DC2626';
    }

    if (model === 'piotroski') {
      const fscore = Math.round(score);
      if (fscore >= 7) return '#059669';
      if (fscore >= 4) return '#D97706';
      return '#DC2626';
    }

    if (model === 'beneish') {
      // M < -2.22 = low risk (green), M >= -2.22 = high risk (red)
      if (score < -2.22) return '#059669';
      return '#DC2626';
    }

    return undefined;
  };

  // Check if coverage indicator should be shown
  const shouldShowCoverage = () => {
    if (!coverage) return false;
    if (model === 'piotroski' && coverage === '9/9') return false;
    if (model === 'beneish' && (coverage === '8/8' || coverage === 'full')) return false;
    // Altman doesn't have partial coverage
    if (model === 'altman') return false;
    // Show for partial coverage
    return coverage !== 'insufficient';
  };

  const scoreColor = getScoreColor();
  const formattedScore = formatScore();
  const showCoverage = shouldShowCoverage();

  return (
    <span className="inline-flex items-center">
      <span style={scoreColor ? { color: scoreColor } : undefined}>
        {formattedScore}
      </span>
      {showCoverage && (
        <span className="text-xs ml-1" style={{ color: '#9CA3AF' }}>
          {/* Two non-breaking spaces before coverage */}
          {'\u00A0\u00A0'}{coverage}
        </span>
      )}
    </span>
  );
};

export default ScoringBadge;
